"use client";

import * as React from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { Jurisdiction } from "@/lib/simulator/tax";

import { IncomeInput } from "./income-input";
import { JurisdictionSelect } from "./jurisdiction-select";

/* ──────────────────────────────────────────────────────────────────────────
 * TaxSettingsModal — opens from a small "Tax settings" link in the inputs
 * column. Houses Annual Income + Province / Territory inputs so they don't
 * clutter the main canvas.
 *
 * Slice 11 polish — the main inputs the user is actually playing with
 * (amount, lending type, stock, time period) stay in the workbench. Tax
 * settings are entered once and rarely changed, so they live behind a click.
 *
 * Live-updates pattern: input changes flow through immediately (no Apply
 * button). The footer Close just dismisses the dialog. The trigger button
 * shows a status snippet — e.g. "Income $150,000 · BC" — so the user can
 * see current settings without opening the modal.
 * ────────────────────────────────────────────────────────────────────────── */

const formatIncome = (income: number) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(income);

export interface TaxSettingsModalProps {
  income: number | undefined;
  onIncomeChange: (next: number | undefined) => void;
  jurisdiction: Jurisdiction;
  onJurisdictionChange: (next: Jurisdiction) => void;
  jurisdictionLabel: string;
}

export function TaxSettingsModal({
  income,
  onIncomeChange,
  jurisdiction,
  onJurisdictionChange,
  jurisdictionLabel,
}: TaxSettingsModalProps) {
  const summary =
    income !== undefined && income > 0
      ? `${formatIncome(income)} · ${jurisdiction}`
      : "Set income & province";

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 text-xs text-zinc-600 underline-offset-4 hover:text-zinc-900 hover:underline"
        >
          <span className="font-mono tracking-[0.18em] uppercase">Tax settings</span>
          <span aria-hidden className="text-zinc-400">
            ·
          </span>
          <span className="tabular-nums">{summary}</span>
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Tax settings</DialogTitle>
          <DialogDescription>
            Your income and province affect the potential tax refund estimate. Changes apply
            immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 py-2">
          <div>
            <p className="font-mono text-[10px] font-medium tracking-[0.18em] text-zinc-500 uppercase">
              Annual income
            </p>
            <div className="mt-1.5">
              <IncomeInput value={income} onChange={onIncomeChange} />
            </div>
            <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">
              Combined household income, before tax.
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] font-medium tracking-[0.18em] text-zinc-500 uppercase">
              Province / territory
            </p>
            <div className="mt-1.5">
              <JurisdictionSelect value={jurisdiction} onChange={onJurisdictionChange} />
            </div>
            <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">
              {jurisdictionLabel === "British Columbia"
                ? "Currently using British Columbia brackets."
                : `Coming soon — using British Columbia rates as an estimate for now. Bracket thresholds vary; your actual rate may differ by a few percentage points.`}
            </p>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Done
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
